/**
 * `NoOrgsScreen` — GVW-160 standalone state page.
 *
 * First-run empty state when the user's account is not in any organization.
 * Per copy-deck.md gvw.160.* and error-catalog.md entry patterns.
 * Uses design-system tokens only, no literal colors/sizes.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-160/brief.md
 */

import { useEffect } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function NoOrgsScreen() {
  const liveRegion = useLiveRegion();

  useEffect(() => {
    // Copy key: gvw.160.title from copy-deck.md
    document.title = 'No organizations yet · GeoViewer';
    liveRegion.announce('status', 'No organizations yet');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="no-orgs-heading"
      data-gvw-id="GVW-160"
      data-route="/no-orgs"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.ink.secondary,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          business
        </div>

        {/* Copy key: gvw.160.title */}
        <h1
          id="no-orgs-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          No organizations yet
        </h1>

        {/* Copy key: gvw.160.body */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '42ch',
          }}
        >
          Your account is not in any organization. Ask your administrator to add you, or request access below.
        </p>

        {/* Recovery action per brief.md §8 navigation-map.md §8 */}
        <button
          type="button"
          onClick={() => {
            // Cross-app to platform Admin per brief.md §10
            window.open('/admin/request-access', '_blank');
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 18,
            }}
          >
            add
          </span>
          Request access
        </button>
      </section>
    </main>
  );
}